import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { columnDefinitionOutputToColumnDefinitionInput } from '@/tableDefinitionEditor/tableDefinition.utilities';
import type { Column } from 'ag-grid-community';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDefinitionBulkEditInputV1 } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';

/** Preview column for edit-template flow; sourceColumnId links back to the selected table column. */
export type EditTemplatePreviewColumn = ColumnDefinitionBulkEditInputV1 & { sourceColumnId?: string };

export const getPreviewColumnId = (
  previewColumn: ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1,
): string | undefined => ('columnId' in previewColumn ? previewColumn.columnId : undefined);

export const getPreviewSourceColumnId = (previewColumn: EditTemplatePreviewColumn): string | undefined =>
  previewColumn.sourceColumnId;

/**
 * Finds the preview-column entry that represents the given column during edit-template.
 *
 * A preview column is considered a match when either:
 * - its `columnId` matches the persisted/preview column id, or
 * - its `sourceColumnId` matches it (i.e. the preview was created from that source column).
 *
 * Used when re-opening the column editor in edit-template mode so the form reflects the user's
 * in-flight preview edits instead of the persisted column definition.
 */
export const findPreviewColumnForColumnId = (
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined,
  columnId: string | undefined,
): EditTemplatePreviewColumn | undefined => {
  if (!previewColumns?.length || !columnId) {
    return undefined;
  }
  const normalizedColumnId = columnId.toLowerCase();
  return (previewColumns as EditTemplatePreviewColumn[]).find((previewColumn) => {
    const previewColumnId = getPreviewColumnId(previewColumn);
    if (previewColumnId && previewColumnId.toLowerCase() === normalizedColumnId) {
      return true;
    }
    const sourceColumnId = getPreviewSourceColumnId(previewColumn);
    return !!sourceColumnId && sourceColumnId.toLowerCase() === normalizedColumnId;
  });
};

/** True when the preview column is a separate new-output column (not an in-place edit of an existing column). */
export const isNewEditTemplatePreviewColumn = (
  previewColumn: ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1,
): previewColumn is EditTemplatePreviewColumn => !!getPreviewSourceColumnId(previewColumn as EditTemplatePreviewColumn);

/** True when the preview column is a separate new-intermediate column (hidden by default in the grid). */
export const isEditTemplateIntermediatePreviewColumn = (
  previewColumn: ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1,
): boolean =>
  isNewEditTemplatePreviewColumn(previewColumn) && 'isHidden' in previewColumn && previewColumn.isHidden === true;

/** True when the preview column is an in-place hidden preview of a persisted intermediate column. */
export const isExistingIntermediateEditTemplatePreviewColumn = (
  previewColumn: ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1,
): boolean =>
  !isNewEditTemplatePreviewColumn(previewColumn) && 'isHidden' in previewColumn && previewColumn.isHidden === true;

/** Resolves persisted column ids for edit-template preview column definitions. */
export const resolveEditTemplatePreviewColumnIds = (
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined,
  columns: ColumnDefinition[],
): string[] => {
  if (!previewColumns?.length) {
    return [];
  }

  return previewColumns
    .map((previewColumn) => {
      const previewColumnId = getPreviewColumnId(previewColumn);
      if (previewColumnId) {
        return previewColumnId;
      }
      const previewColumnName = 'columnName' in previewColumn ? previewColumn.columnName : undefined;
      if (!previewColumnName) {
        return undefined;
      }
      return columns.find((column) => column.columnName === previewColumnName)?.id;
    })
    .filter((columnId): columnId is string => !!columnId);
};

/** Resolves grid column ids for newly added preview columns (separate output and intermediate columns). */
export const resolveNewEditTemplatePreviewColumnIds = (
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined,
  columns: ColumnDefinition[],
): string[] => resolveEditTemplatePreviewColumnIds(previewColumns?.filter(isNewEditTemplatePreviewColumn), columns);

/** True when a preview column is currently visible in the edit-template grid. */
export const isEditTemplatePreviewColumnVisibleInGrid = (
  previewColumn: ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1,
  displayColumns: ColumnDefinition[],
): boolean => {
  const [columnId] = resolveEditTemplatePreviewColumnIds([previewColumn], displayColumns);
  if (!columnId) {
    return false;
  }

  const displayColumn = displayColumns.find((column) => column.id.toLowerCase() === columnId.toLowerCase());
  return displayColumn ? !displayColumn.isHidden : false;
};

/** Resolves grid column ids for newly added preview columns that should show warning header styling. */
export const resolveHighlightedNewEditTemplatePreviewColumnIds = (
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined,
  displayColumns: ColumnDefinition[],
): string[] =>
  resolveEditTemplatePreviewColumnIds(
    previewColumns?.filter(
      (previewColumn) =>
        isNewEditTemplatePreviewColumn(previewColumn) &&
        (!isEditTemplateIntermediatePreviewColumn(previewColumn) ||
          isEditTemplatePreviewColumnVisibleInGrid(previewColumn, displayColumns)),
    ),
    displayColumns,
  );

/** Applies Ag Grid column visibility to edit-template preview table columns (local preview state only). */
export const applyAgGridVisibilityToEditTemplatePreviewTableColumns = (
  previewTableColumns: ColumnDefinition[],
  agColumns: Column[],
): ColumnDefinition[] => {
  const visibilityByColumnId = new Map(
    agColumns
      .map((agColumn) => {
        const columnId = agColumn.getColDef().field?.toLowerCase();
        return columnId ? ([columnId, agColumn.isVisible()] as const) : undefined;
      })
      .filter((entry): entry is readonly [string, boolean] => !!entry),
  );

  return previewTableColumns.map((column) => {
    const isVisible = visibilityByColumnId.get(column.id.toLowerCase());
    if (isVisible === undefined) {
      return column;
    }
    return { ...column, isHidden: !isVisible };
  });
};

const formatPersistedColumnAsPreviewBulkEdit = (
  column: ColumnDefinition,
  columns: ColumnDefinition[],
): ColumnDefinitionBulkEditInputV1 =>
  columnDefinitionOutputToColumnDefinitionInput({ ...column, displayProperty: SeeqNames.Properties.Name }, columns);

const toComparablePreviewColumn = (
  previewColumn: ColumnDefinitionBulkEditInputV1 | EditTemplatePreviewColumn,
): ColumnDefinitionBulkEditInputV1 => {
  if (!('sourceColumnId' in previewColumn)) {
    return previewColumn;
  }
  const { sourceColumnId: _sourceColumnId, ...comparable } = previewColumn;
  return comparable;
};

const toComparablePreviewColumnForChangeDetection = (
  previewColumn: ColumnDefinitionBulkEditInputV1 | EditTemplatePreviewColumn,
): ColumnDefinitionBulkEditInputV1 => {
  const { isHidden: _isHidden, ...comparable } = toComparablePreviewColumn(previewColumn);
  return comparable;
};

/** True when an in-place preview column differs from the persisted table column it represents. */
export const hasEditTemplatePreviewColumnChanged = (
  previewColumn: ColumnDefinitionBulkEditInputV1,
  columns: ColumnDefinition[],
): boolean => {
  const columnId = getPreviewColumnId(previewColumn);
  if (!columnId) {
    return true;
  }

  const persistedColumn = columns.find((column) => column.id.toLowerCase() === columnId.toLowerCase());
  if (!persistedColumn) {
    return true;
  }

  const baselinePreviewColumn = formatPersistedColumnAsPreviewBulkEdit(persistedColumn, columns);
  return !_.isEqual(
    toComparablePreviewColumnForChangeDetection(previewColumn),
    toComparablePreviewColumnForChangeDetection(baselinePreviewColumn),
  );
};

/** Resolves grid column ids for existing outputs edited in place during template edit. */
export const resolveEditedEditTemplatePreviewColumnIds = (
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined,
  columns: ColumnDefinition[],
): string[] => {
  if (!previewColumns?.length) {
    return [];
  }

  return previewColumns
    .filter(
      (previewColumn) =>
        !isNewEditTemplatePreviewColumn(previewColumn) &&
        !isExistingIntermediateEditTemplatePreviewColumn(previewColumn) &&
        hasEditTemplatePreviewColumnChanged(previewColumn, columns),
    )
    .map((previewColumn) => getPreviewColumnId(previewColumn))
    .filter((columnId): columnId is string => !!columnId);
};

/** Resolves grid column ids that should show edit-template warning header styling. */
export const resolveHighlightedEditTemplatePreviewColumnIds = (
  previewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined,
  persistedColumns: ColumnDefinition[],
  displayColumns: ColumnDefinition[],
): string[] => [
  ...resolveHighlightedNewEditTemplatePreviewColumnIds(previewColumns, displayColumns),
  ...resolveEditedEditTemplatePreviewColumnIds(previewColumns, persistedColumns),
];

export const toNormalizedColumnIdSet = (columnIds: Iterable<string>): Set<string> =>
  new Set([...columnIds].map((id) => id.toLowerCase()));

export const normalizedColumnIdSetHas = (columnIds: Set<string>, columnId: string | undefined): boolean =>
  !!columnId && columnIds.has(columnId.toLowerCase());

/** Keeps removed persisted outputs visible when preview table columns omit them. */
export const mergePreviewTableColumnsWithRemovedOutputs = (
  previewTableColumns: ColumnDefinition[],
  persistedColumns: ColumnDefinition[],
  removedOutputByColumnId: Record<string, unknown> | undefined,
): ColumnDefinition[] => {
  const removedColumnIds = toNormalizedColumnIdSet(Object.keys(removedOutputByColumnId ?? {}));
  if (!removedColumnIds.size) {
    return previewTableColumns;
  }

  const previewColumnIds = toNormalizedColumnIdSet(previewTableColumns.map((column) => column.id));
  const missingRemovedColumns = persistedColumns.filter(
    (column) =>
      normalizedColumnIdSetHas(removedColumnIds, column.id) && !normalizedColumnIdSetHas(previewColumnIds, column.id),
  );

  return missingRemovedColumns.length ? [...previewTableColumns, ...missingRemovedColumns] : previewTableColumns;
};
